import React, { useEffect, useState } from "react";
import { 
  Button, 
  CircularProgress, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Divider,
  Box,
  Alert,
  Autocomplete,
  Tooltip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import OrpenService from "../../../core/http/service/OrpenService";
import Colors from "../../../constants/Colors";
import { VariableRow } from "../OrpenConfig.styled";

interface Props {
  empresaId: string;
  isViewState?: boolean;
}

const SPM_VARIABLES = [
  { label: "Nome do Paciente", value: "{PACIENTE_NOME}" },
  { label: "Data e Hora do Agendamento (Juntos)", value: "{HORARIO}" },
  { label: "Data do Agendamento (Separado)", value: "{DATA}" },
  { label: "Hora do Agendamento (Separado)", value: "{HORA}" },
  { label: "Nome do Profissional", value: "{PROFISSIONAL}" },
  { label: "ID do Agendamento", value: "{AGENDAMENTO_ID}" },
  { label: "Link de Confirmação", value: "{LINK_CONFIRMAR}" },
  { label: "Link de Cancelamento", value: "{LINK_CANCELAR}" },
];

const OrpenTemplates = ({ empresaId, isViewState = false }: Props) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [configId, setConfigId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [message, setMessage] = useState({ text: "", type: "success" as "success" | "error" });

  useEffect(() => {
    fetchData();
  }, [empresaId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const configResp: any = await OrpenService.getConfiguracao(empresaId);
      if (configResp.data && configResp.data.data) {
        setConfigId(configResp.data.data.id);
        const templatesResp: any = await OrpenService.getTemplates(configResp.data.data.id);
        setTemplates(templatesResp.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching Orpen templates", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (template: any = null) => {
    if (template) {
      const sortedVars = [...(template.variaveis || [])].sort((a: any, b: any) => a.index - b.index);
      setEditingTemplate({
        ...template,
        variaveis: sortedVars
      });
    } else {
      setEditingTemplate({
        orpenConfiguracaoId: configId,
        templateId: "",
        nome: "",
        contentText: "",
        ativo: true,
        variaveis: []
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingTemplate(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ text: "", type: "success" });
    try {
      await OrpenService.saveTemplate(editingTemplate);
      setMessage({ text: "Template salvo com sucesso!", type: "success" });
      handleClose();
      fetchData();
    } catch (error) {
      setMessage({ text: "Erro ao salvar template.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setMessage({ text: "", type: "success" });
    if (window.confirm("Tem certeza que deseja excluir este template?")) {
      try {
        await OrpenService.deleteTemplate(id);
        setMessage({ text: "Template excluído!", type: "success" });
        fetchData();
      } catch (error) {
        setMessage({ text: "Erro ao excluir template.", type: "error" });
      }
    }
  };

  const addVariable = () => {
    const nextIndex = editingTemplate.variaveis.length + 1;
    setEditingTemplate({
      ...editingTemplate,
      variaveis: [...editingTemplate.variaveis, { index: nextIndex, nome: `Variavel ${nextIndex}`, mappingField: "" }]
    });
  };

  const removeVariable = (index: number) => {
    const newVars = editingTemplate.variaveis.filter((_: any, i: number) => i !== index)
      .map((v: any, i: number) => ({ ...v, index: i + 1 }));
    setEditingTemplate({ ...editingTemplate, variaveis: newVars });
  };

  const moveVariableUp = (index: number) => {
    if (index === 0) return;
    const newVars = [...editingTemplate.variaveis];
    const temp = newVars[index];
    newVars[index] = newVars[index - 1];
    newVars[index - 1] = temp;
    
    const reindexedVars = newVars.map((v: any, i: number) => ({ ...v, index: i + 1 }));
    setEditingTemplate({ ...editingTemplate, variaveis: reindexedVars });
  };

  const moveVariableDown = (index: number) => {
    if (index === editingTemplate.variaveis.length - 1) return;
    const newVars = [...editingTemplate.variaveis];
    const temp = newVars[index];
    newVars[index] = newVars[index + 1];
    newVars[index + 1] = temp;
    
    const reindexedVars = newVars.map((v: any, i: number) => ({ ...v, index: i + 1 }));
    setEditingTemplate({ ...editingTemplate, variaveis: reindexedVars });
  };

  if (loading) return <CircularProgress />;
  if (!configId) return <Typography color="error">Configure a Orpen Geral antes de gerenciar templates.</Typography>;

  return (
    <div>
      {message.text && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={message.type} onClose={() => setMessage({ ...message, text: "" })}>
            {message.text}
          </Alert>
        </Box>
      )}

      {!isViewState && (
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />} 
          onClick={() => handleOpen()}
          sx={{ mb: 2 }}
        >
          Novo Template
        </Button>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Template ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((t: any) => (
              <TableRow key={t.id}>
                <TableCell>{t.nome}</TableCell>
                <TableCell>{t.templateId}</TableCell>
                <TableCell>{t.ativo ? "Ativo" : "Inativo"}</TableCell>
                <TableCell align="right">
                  {isViewState ? (
                    <IconButton onClick={() => handleOpen(t)} color="primary" title="Visualizar Template">
                      <VisibilityIcon />
                    </IconButton>
                  ) : (
                    <>
                      <IconButton onClick={() => handleOpen(t)} color="primary" title="Editar Template">
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(t.id)} color="error" title="Excluir Template">
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: Colors.SPM50, borderBottom: `1px solid ${Colors.SPM100}` }}>
          {isViewState ? "Visualizar Template" : editingTemplate?.id ? "Editar Template" : "Novo Template"}
        </DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Nome do Template"
                fullWidth
                required
                disabled={isViewState}
                value={editingTemplate?.nome || ""}
                onChange={(e) => setEditingTemplate({ ...editingTemplate, nome: e.target.value })}
              />
              <TextField
                label="Orpen Template ID"
                fullWidth
                required
                disabled={isViewState}
                value={editingTemplate?.templateId || ""}
                onChange={(e) => setEditingTemplate({ ...editingTemplate, templateId: e.target.value })}
              />
            </Box>

            <TextField
              label="Texto de Apoio (content_text)"
              fullWidth
              multiline
              rows={2}
              disabled={isViewState}
              value={editingTemplate?.contentText || ""}
              onChange={(e) => setEditingTemplate({ ...editingTemplate, contentText: e.target.value })}
              helperText="Este campo é apenas um texto informativo interno para a Orpen."
            />
            
            <Divider>
              <Typography variant="overline" color="textSecondary">Configuração de Variáveis</Typography>
            </Divider>

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">Variáveis Dinâmicas</Typography>
                  <Tooltip title="Mapeie as variáveis do seu template na Orpen com os dados reais do SPM.">
                    <InfoIcon fontSize="small" color="action" />
                  </Tooltip>
                </Box>
                {!isViewState && (
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={addVariable} 
                    startIcon={<AddIcon />}
                  >
                    Adicionar Variável
                  </Button>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {editingTemplate?.variaveis.map((v: any, i: number) => (
                  <Paper 
                    key={i} 
                    variant="outlined" 
                    sx={{ p: 1.5, borderLeft: `4px solid ${Colors.SPM500}`, bgcolor: '#fafafa' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="subtitle2" sx={{ minWidth: 25, fontWeight: 'bold', color: Colors.SPM600 }}>
                        #{v.index}
                      </Typography>
                      
                      <TextField
                        label="Nome na Orpen"
                        placeholder="Ex: Variavel 1"
                        size="small"
                        disabled={isViewState}
                        value={v.nome || ""}
                        sx={{ flex: 1, bgcolor: 'white' }}
                        onChange={(e) => {
                          const newVars = [...editingTemplate.variaveis];
                          newVars[i].nome = e.target.value;
                          setEditingTemplate({ ...editingTemplate, variaveis: newVars });
                        }}
                      />

                      <Autocomplete
                        freeSolo
                        disabled={isViewState}
                        options={SPM_VARIABLES}
                        getOptionLabel={(option) => typeof option === 'string' ? option : `${option.label} (${option.value})`}
                        value={v.mappingField || ""}
                        onChange={(event, newValue) => {
                          const val = typeof newValue === 'string' ? newValue : (newValue?.value || "");
                          const newVars = [...editingTemplate.variaveis];
                          newVars[i].mappingField = val;
                          setEditingTemplate({ ...editingTemplate, variaveis: newVars });
                        }}
                        onInputChange={(event, newInputValue) => {
                          const newVars = [...editingTemplate.variaveis];
                          newVars[i].mappingField = newInputValue;
                          setEditingTemplate({ ...editingTemplate, variaveis: newVars });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Mapeamento SPM"
                            placeholder="Selecione ou digite..."
                            size="small"
                            disabled={isViewState}
                          />
                        )}
                        sx={{ flex: 1.5, bgcolor: 'white' }}
                      />

                      {!isViewState && (
                        <>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton 
                              onClick={() => moveVariableUp(i)} 
                              disabled={i === 0} 
                              size="small"
                              title="Mover para cima"
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              onClick={() => moveVariableDown(i)} 
                              disabled={i === editingTemplate.variaveis.length - 1} 
                              size="small"
                              title="Mover para baixo"
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </Box>

                          <IconButton onClick={() => removeVariable(i)} color="error" size="small" title="Excluir variável">
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Paper>
                ))}
                
                {editingTemplate?.variaveis.length === 0 && (
                  <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 2, fontStyle: 'italic' }}>
                    Nenhuma variável adicionada.
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f5f5f5' }}>
          {isViewState ? (
            <Button onClick={handleClose} variant="contained" color="primary">
              Fechar
            </Button>
          ) : (
            <>
              <Button onClick={handleClose} color="inherit" disabled={saving}>Cancelar</Button>
              <Button 
                onClick={handleSave} 
                variant="contained" 
                color="primary"
                disabled={saving || !editingTemplate?.nome || !editingTemplate?.templateId}
              >
                {saving ? <CircularProgress size={24} color="inherit" /> : "Salvar Template"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OrpenTemplates;
